import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationResults = ({ result, onVerifyAnother, onExportReport }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'warning': return 'text-warning';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'failed': return 'XCircle';
      default: return 'HelpCircle';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified': return 'bg-success/10 text-success border-success/20';
      case 'warning': return 'bg-warning/10 text-warning border-warning/20';
      case 'failed': return 'bg-destructive/10 text-destructive border-destructive/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    return 'text-destructive';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = () => {
    const exportData = {
      verificationReport: {
        ...result,
        exportedAt: new Date()?.toISOString(),
        exportFormat
      }
    };

    // Simulate export functionality
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification-report-${result?.verificationId}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);

    onExportReport?.(exportData);
  };

  const getStepStatusIcon = (status) => {
    switch (status) {
      case 'passed': return 'CheckCircle';
      case 'failed': return 'XCircle';
      default: return 'Clock';
    }
  };

  const getStepStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'text-success';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Result Card */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                result?.overallStatus === 'verified' ? 'bg-success/10' :
                result?.overallStatus === 'warning'? 'bg-warning/10' : 'bg-destructive/10'
              }`}>
                <Icon 
                  name={getStatusIcon(result?.overallStatus)} 
                  size={32} 
                  className={getStatusColor(result?.overallStatus)}
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {result?.overallStatus === 'verified' ? 'Credential Verified' :
                   result?.overallStatus === 'warning' ? 'Verification Warning' :
                   'Verification Failed'}
                </h2>
                <p className="text-muted-foreground mt-1">
                  Verification completed on {formatDate(result?.timestamp)}
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium ${
                getStatusBadge(result?.overallStatus)
              }`}>
                {result?.overallStatus?.toUpperCase()}
              </div>
              <div className="mt-2">
                <span className="text-sm text-muted-foreground">Confidence: </span>
                <span className={`font-semibold ${getConfidenceColor(result?.confidence)}`}>
                  {Math.round(result?.confidence)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Credential Summary */}
        <div className="p-6 border-b border-border bg-muted/30">
          <h3 className="font-semibold text-foreground mb-4">Credential Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Credential ID</p>
              <p className="font-medium text-foreground truncate">{result?.credentialId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Type</p>
              <p className="font-medium text-foreground">
                {result?.credential?.type || 'Verifiable Credential'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Issuer</p>
              <p className="font-medium text-foreground">
                {result?.credential?.issuer || 'Unknown Issuer'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Input Method</p>
              <p className="font-medium text-foreground capitalize">
                {result?.inputMethod?.replace('_', ' ') || 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* Verification Summary */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-success">{result?.summary?.passedSteps}</p>
              <p className="text-sm text-muted-foreground">Steps Passed</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Icon name="XCircle" size={24} className="text-destructive" />
              </div>
              <p className="text-2xl font-bold text-destructive">{result?.summary?.failedSteps}</p>
              <p className="text-sm text-muted-foreground">Steps Failed</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Icon name="Clock" size={24} className="text-primary" />
              </div>
              <p className="text-2xl font-bold text-primary">
                {Math.round(result?.summary?.processingTime / 1000)}s
              </p>
              <p className="text-sm text-muted-foreground">Processing Time</p>
            </div>
          </div>
        </div>
      </div>
      {/* Detailed Results */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <Button
            variant="ghost"
            onClick={() => setShowDetails(!showDetails)}
            iconName={showDetails ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="w-full justify-between"
          >
            <span className="font-semibold">Detailed Verification Results</span>
          </Button>
        </div>

        {showDetails && (
          <div className="p-4">
            <div className="space-y-4">
              {Object.entries(result?.stepResults)?.map(([stepId, stepResult]) => (
                <div key={stepId} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={getStepStatusIcon(stepResult?.status)} 
                        size={20} 
                        className={getStepStatusColor(stepResult?.status)}
                      />
                      <h4 className="font-medium text-foreground capitalize">
                        {stepId?.replace(/_/g, ' ')}
                      </h4>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      stepResult?.status === 'passed' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                    }`}>
                      {stepResult?.status}
                    </span>
                  </div>

                  <div className="text-sm text-muted-foreground mb-2">
                    Completed: {formatDate(stepResult?.timestamp)}
                  </div>

                  {stepResult?.details && (
                    <div className="bg-muted/50 rounded-md p-3 mt-3">
                      <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                        {JSON.stringify(stepResult?.details, null, 2)}
                      </pre>
                    </div>
                  )}

                  {stepResult?.error && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mt-3">
                      <p className="text-sm text-destructive">{stepResult?.error}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          variant="default"
          onClick={onVerifyAnother}
          iconName="RotateCcw"
          iconPosition="left"
          className="flex-1"
        >
          Verify Another Credential
        </Button>
        
        <Button
          variant="outline"
          onClick={handleExport}
          iconName="Download"
          iconPosition="left"
          className="flex-1"
        >
          Export Report
        </Button>
        
        <Button
          variant="secondary"
          onClick={() => window.location.href = '/digital-wallet'}
          iconName="Wallet"
          iconPosition="left"
          className="flex-1"
        >
          View Wallet
        </Button>
      </div>
      {/* Additional Actions */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <h4 className="font-medium text-foreground mb-3">Additional Actions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="ghost"
            size="sm"
            iconName="Share"
            iconPosition="left"
            className="justify-start"
          >
            Share Result
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Copy"
            iconPosition="left"
            className="justify-start"
          >
            Copy Report
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Mail"
            iconPosition="left"
            className="justify-start"
          >
            Email Report
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Archive"
            iconPosition="left"
            className="justify-start"
          >
            Archive Result
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerificationResults;